import React from 'react';

import Advantage from 'pages/Landings/components/Advantage';
import Advantages from 'pages/Landings/components/Advantages';
import { BlocksIcon, DataScaleIcon, ExploitsAndKevIcon, LampIcon, SignalIcon } from 'pages/Landings/components/Icons';
import Paragraph from 'pages/Landings/components/Paragraph';
import TopicContent from 'pages/Landings/components/TopicContent';
import TopicHeading from 'pages/Landings/components/TopicHeading';
import TopicTemplate from 'pages/Landings/components/TopicTemplate';

const ExploitsAndKev: React.FC = () => {
    return (
        <TopicTemplate
            heading={
                <TopicHeading
                    title="Vulners Exploits & KEV"
                    description="Prioritize What’s Actively Dangerous"
                    illustration={<ExploitsAndKevIcon />}
                />
            }
            content={
                <TopicContent>
                    <Paragraph
                        primary="Prioritize What’s Actively Dangerous"
                        secondary="Vulners Exploits & KEV puts exploitation evidence at the center of your attention. We don’t just mirror CVSS; we surface public PoCs, weaponization signals, and CISA KEV status next to each CVE - so you fix what’s being attacked right now, not what only looks scary on paper. Our coverage blends first-party parsing of GitHub and Gitee published exploits and PoC with a curated mesh of established exploit feeds, then correlates it all with your vulnerability intelligence."
                    />
                    <Paragraph
                        primary="Unique Exploit Intelligence You Can’t Get Elsewhere"
                        secondary="Vulners runs custom collectors for GitHub and Gitee to pull real PoCs and tooling directly from source, with smart filtering and de-duplication to keep the noise down. On top of that, we unify 10+ exploit sources—including Exploit-DB, Metasploit, Packet Storm, Seebug, 0day.today (1337DAY), Zero Science Lab, and Exploit Pack —into one consistent stream with source provenance you can verify."
                    />
                    <Paragraph
                        primary="Advanced Vulnerability Intelligence Integration"
                        secondary="Exploit signals are correlated with the rest of your risk picture - KEV status, timelines, references, and standard metrics (CVSS/EPSS/CWE) - returned as clean JSON for instant UI and automation use. Vulners maintains a correlated database spanning 200+ sources and millions of records, so your product stays current without chasing feeds."
                    />
                    <Paragraph
                        primary="New Revenue Streams & Flexible Integration"
                        secondary="Package real-world risk as premium value - under your brand. Add “Exploitation in Progress” banners, KEV-driven SLAs, exploit-aware CI/CD gates, targeted alerts per tech stack, and heat maps of active threats across assets. OEM-friendly delivery lets you ship this natively without building a threat-intel team from scratch."
                    />
                </TopicContent>
            }
            advantages={
                <Advantages
                    title={
                        <>
                            {'Why Vulners?'}
                            <br />
                            {'Unmatched Exploit Coverage & Context'}
                        </>
                    }
                >
                    <Advantage
                        icon={<LampIcon />}
                        title="First-party PoC harvesting"
                        description="Purpose-built parsers for GitHub and Gitee PoCs with smart filtering, de-duplication, and source links for verification."
                    />
                    <Advantage
                        icon={<DataScaleIcon />}
                        title="Breadth of exploit sources"
                        description="Unified coverage across Exploit-DB, Metasploit, Packet Storm, Seebug, 0day.today (1337DAY), Zero Science Lab, Exploit Pack, and more."
                    />
                    <Advantage
                        icon={<SignalIcon />}
                        title="Correlated, credible signals"
                        description="Exploit status and KEV alongside CVEs, timelines, and risk metrics - mapped via Vulners’ standardized fields (e.g., explicit ExploitDB/Metasploit types) for reliable downstream use."
                    />
                    <Advantage
                        icon={<BlocksIcon />}
                        title="Built for builders"
                        description="Add the high-signal data your customers actually feel—without detouring your team into feed wrangling and normalization."
                    />
                </Advantages>
            }
        />
    );
};

export default ExploitsAndKev;
